import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Grid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { useCreatePatientMutation } from '@/store/slices/api/patient/patientListApiSlice';
import { routes } from '@/routes';

import { PatientSex } from '@/types/patientList';
import CustomModal from '@/ui/Modal';

import { DeviceVendors } from '../analytics/constants';
import DeviceInfoFields from '../CreateDevice/DeviceInfoFields';

import styles from './CreatePatientModal.module.scss';
import PatientInfoFields from './PatientInfoFields';

export interface CreatePatientFormValues {
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  sex: string;
  mrn: string;
  clinicId: string;
  vendor: string;
  type: string;
  serialNumber: string;
  modelName?: string;
  modelNumber?: string;
  implantDate: string;
}

const CreatePatientModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [isError, setIsError] = useState(false);
  const [errorType, setErrorType] = useState<'Patient' | 'Device' | false>(false);
  const [errorPatientId, setErrorPatientId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { user: currentUser } = useAppSelector((state: RootState) => state.auth);
  const defaultClinicId = currentUser?.organizations ? currentUser?.organizations[0].id : '';

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isValid }
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        firstName: Yup.string().required('First name is required'),
        middleName: Yup.string(),
        lastName: Yup.string().required('Last name is required'),
        dob: Yup.string().required('Date of birth is required'),
        sex: Yup.string().required('Sex is required'),
        mrn: Yup.string().required('Internal MRN is required'),
        clinicId: Yup.string().required('Clinic is required'),
        vendor: Yup.string().required('Manufacturer is required'),
        type: Yup.string().required('Type is required'),
        serialNumber: Yup.string().required('Serial number is required'),
        modelName: Yup.string().when('vendor', {
          is: (val: string) => val?.toLowerCase() === 'biotronik',
          then: (schema) => schema.required('Model name is required for Biotronik'),
          otherwise: (schema) => schema.optional()
        }),
        modelNumber: Yup.string().when('vendor', {
          is: (val: string) => val?.toLowerCase() !== 'biotronik',
          then: (schema) => schema.required('Model number is required unless vendor is Biotronik'),
          otherwise: (schema) => schema.optional()
        }),
        implantDate: Yup.string().required('Implant date is required')
      })
    ),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      sex: '',
      mrn: '',
      clinicId: defaultClinicId,
      vendor: '',
      type: '',
      serialNumber: '',
      modelName: '',
      modelNumber: '',
      implantDate: ''
    }
  });

  const [createPatient] = useCreatePatientMutation();
  const vendor = watch('vendor');

  const onSubmit = async (values: CreatePatientFormValues) => {
    setIsLoading(true);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: dayjs(values.dob).format('YYYY-MM-DDT00:00:00'),
      sex: values.sex as PatientSex,
      internalMRN: values.mrn,
      clinicId: values.clinicId,
      device: {
        deviceVendor: values.vendor as DeviceVendors,
        deviceType: values.type,
        modelName: values.modelName ?? '',
        modelNumber: values.modelNumber ?? '',
        serialNumber: values.serialNumber,
        implantDate: dayjs(values.implantDate).format('YYYY-MM-DDT00:00:00')
      }
    };

    try {
      const { data } = await createPatient(payload).unwrap();

      if (!data.success) {
        setIsError(true);
        setErrorType(data.message.includes('Patient already exists') ? 'Patient' : 'Device');
        setErrorPatientId(data.patient?.id ?? '');
        setIsLoading(false);
        return;
      }

      reset();
      onClose();
      setIsError(false);
      navigate(routes.physician.patientDetail(data.patient?.id ?? ''));
      setIsLoading(false);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create patient.',
        color: 'red',
        position: 'bottom-right'
      });
      setIsLoading(false);
      console.log({ err });
    }
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose: () => {
          if (isError) {
            setIsError(false);
            setErrorType(false);
            setErrorPatientId('');
          } else {
            reset();
            onClose();
          }
        },
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: true
      }}
      header={{ title: isError ? 'Error Creating Patient' : 'Create Patient' }}
      secondaryButton={{
        text: isError ? 'Back' : 'Cancel',
        onClick: () => {
          if (isError) {
            setIsError(false);
            setErrorType(false);
            setErrorPatientId('');
          } else {
            reset();
            onClose();
          }
        }
      }}
      primaryButton={{
        text: isError ? 'Close' : 'Create',
        onClick: () => {
          if (isError) {
            setIsError(false);
            setErrorType(false);
            setErrorPatientId('');
            reset();
            onClose();
          } else {
            handleSubmit(onSubmit)();
          }
        },
        isDisabled: (!isError && !isValid) || isLoading,
        isLoading: isLoading
      }}>
      {isError ? (
        <>
          <div className={styles.errorMessage}>
            {errorType === 'Patient'
              ? 'A patient already exists with the same Name, Date of Birth or MRN.'
              : 'A device already exists with the same manufacturer and serial number.'}
          </div>
          <br />
          <button
            onClick={() => {
              onClose();
              setIsError(false);
              setErrorType(false);
              navigate(routes.physician.patientDetail(errorPatientId ?? ''));
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#0d6efd',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 500
            }}>
            View patient profile
          </button>
        </>
      ) : (
        <div className={styles.reportForm}>
          <PatientInfoFields control={control} />

          {/* Divider */}
          <Divider my="md" />

          {/* Device Info */}
          <div className={styles.sectionTitle}>Device Information</div>
          <Grid>
            <DeviceInfoFields control={control} vendor={vendor} />
          </Grid>
        </div>
      )}
    </CustomModal>
  );
};

export default CreatePatientModal;
